import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, Lock, Chrome, UserPlus, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackMetaEvent } from "@/lib/metaPixel";

const SIDE_IMG = "https://images.unsplash.com/photo-1678366633407-7f49da199a42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBlbGVnYW50JTIwM2QlMjBzaGFwZXN8ZW58MHx8fHwxNzg3OTkwNzk0fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(() =>
    new URLSearchParams(location.search).get("mode") === "inscription" ? "register" : "login"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);

  if (!loading && user) return <Navigate to="/app" replace />;

  const isRegister = mode === "register";
  const isForgot = mode === "forgot";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (isForgot) {
      setSubmitting(true);
      try {
        await api.post("/auth/forgot-password", {
          email: email.trim(),
          reset_url_base: window.location.origin + "/reinitialiser-mot-de-passe",
        });
        setResetSent(true);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) setError("La réinitialisation est en cours d'activation côté serveur — réessayez bientôt.");
        else setError("Envoi impossible pour le moment. Réessayez.");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (isRegister) {
      if (password.length < 8) {
        setError("Le mot de passe doit contenir au moins 8 caractères.");
        return;
      }
      if (password !== confirm) {
        setError("Les deux mots de passe ne correspondent pas.");
        return;
      }
    }
    setSubmitting(true);
    try {
      if (isRegister) {
        await register(email.trim(), password);
        trackMetaEvent("CompleteRegistration");
        toast.success("Compte créé — bienvenue sur FacturXpress");
      } else {
        await login(email.trim(), password);
        toast.success("Connexion réussie");
      }
      const dest = location.state?.from?.pathname || "/app";
      navigate(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : isRegister
            ? "Inscription impossible pour le moment."
            : "Connexion impossible. Vérifiez vos identifiants.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "w-full rounded-xl bg-black/40 border border-white/10 pl-11 pr-4 py-3.5 text-sm placeholder:text-zinc-600 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] outline-none transition-[border-color,box-shadow] duration-200";

  const title = isForgot ? "Mot de passe oublié." : isRegister ? "Créez votre compte." : "Bon retour.";
  const subtitle = isForgot
    ? "Saisissez votre e-mail : vous recevrez un lien pour définir un nouveau mot de passe."
    : isRegister
      ? "3 factures offertes pour commencer — sans carte bancaire. Votre compte marche sur le web et l'app mobile."
      : "Utilisez les mêmes identifiants que l'application mobile FacturXpress. Vos factures, vos archives, vos réglages : tout est synchronisé.";

  return (
    <div className="min-h-screen bg-[#050505] text-white grid lg:grid-cols-2" data-testid="login-page">
      <div className="noise-overlay" />
      <div className="flex flex-col justify-center px-6 md:px-16 py-16 relative z-10">
        <Link to="/" data-testid="login-back-home" className="flex items-center gap-2.5 mb-14 w-fit">
          <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#00D4FF] flex items-center justify-center font-display text-[13px] font-extrabold text-[#050505]">FX</span>
          <span className="font-display text-sm tracking-tight">Factur<span className="text-[#00D4FF]">Xpress</span></span>
        </Link>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="overline-label mb-3">{isForgot ? "Récupération" : isRegister ? "Inscription" : "Espace client"}</p>
          <h1 className="font-display font-light tracking-tighter text-4xl md:text-5xl leading-tight">{title}</h1>
          <p className="mt-4 text-zinc-400 text-sm md:text-base max-w-md">{subtitle}</p>

          {isForgot && resetSent ? (
            <div className="mt-10 max-w-md" data-testid="forgot-success">
              <div className="rounded-2xl border border-[#34C759]/40 bg-[#34C759]/10 p-6">
                <p className="text-sm text-[#34C759] font-medium">E-mail envoyé</p>
                <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
                  Si un compte existe pour <span className="font-mono">{email}</span>, un lien de
                  réinitialisation vient d'être envoyé. Vérifiez vos spams si besoin.
                </p>
              </div>
              <button
                onClick={() => { setMode("login"); setResetSent(false); }}
                data-testid="forgot-back-login"
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#00D4FF] hover:underline"
              >
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-10 space-y-5 max-w-md" data-testid="login-form">
              <div>
                <label htmlFor="login-email" className="overline-label block mb-2">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@entreprise.fr"
                    className={inputCls}
                  />
                </div>
              </div>
              {!isForgot && (
                <div>
                  <label htmlFor="login-password" className="overline-label block mb-2">Mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                    <input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      required
                      autoComplete={isRegister ? "new-password" : "current-password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={isRegister ? "8 caractères minimum" : "••••••••"}
                      className={inputCls}
                    />
                  </div>
                  {!isRegister && (
                    <button
                      type="button"
                      onClick={() => { setMode("forgot"); setError(""); }}
                      data-testid="forgot-password-link"
                      className="mt-2 text-xs text-zinc-500 hover:text-[#00D4FF] transition-colors duration-200"
                    >
                      Mot de passe oublié ?
                    </button>
                  )}
                </div>
              )}
              {isRegister && (
                <div>
                  <label htmlFor="login-confirm" className="overline-label block mb-2">Confirmation</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                    <input
                      id="login-confirm"
                      data-testid="login-confirm-input"
                      type="password"
                      required
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Répétez le mot de passe"
                      className={inputCls}
                    />
                  </div>
                </div>
              )}

              {error && (
                <p data-testid="login-error" className="text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-xl px-4 py-3">
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={submitting}
                className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3.5 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-200 disabled:opacity-50 disabled:hover:translate-y-0"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isForgot ? <KeyRound className="h-4 w-4" /> : isRegister ? <UserPlus className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
                {isForgot ? "Envoyer le lien" : isRegister ? "Créer mon compte" : "Se connecter"}
              </button>

              <button
                type="button"
                onClick={() => { setMode(isForgot || isRegister ? "login" : "register"); setError(""); setConfirm(""); }}
                data-testid="auth-mode-toggle"
                className="w-full text-center text-sm text-zinc-400 hover:text-[#00D4FF] transition-colors duration-200 pt-1"
              >
                {isForgot || isRegister ? "Déjà un compte ? Se connecter" : "Pas encore de compte ? Créer un compte"}
              </button>
            </form>
          )}

          {!isForgot && (
            <>
              <div className="mt-6 max-w-md flex items-center gap-4">
                <span className="h-px flex-1 bg-white/10" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">ou</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>

              <button
                onClick={googleLogin}
                data-testid="login-google-button"
                className="mt-6 w-full max-w-md inline-flex justify-center items-center gap-3 rounded-full border border-white/20 px-6 py-3.5 text-sm font-medium text-zinc-200 hover:border-[#00D4FF] hover:text-white hover:-translate-y-0.5 transition-[transform,border-color,color] duration-200"
              >
                <Chrome className="h-4 w-4" />
                Continuer avec Google
              </button>
            </>
          )}
        </motion.div>
      </div>

      <div className="relative hidden lg:block overflow-hidden">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="font-display text-2xl leading-snug max-w-md">
            « Factur-X, PDP, octroi de mer. <span className="text-[#00D4FF]">Une seule connexion.</span> »
          </p>
          <div className="mt-8 flex gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/10 max-w-md">
            {[["Factur-X", "EN 16931"], ["PDP", "Cycle de vie"], ["DOM-TOM", "Octroi natif"]].map(([v, l]) => (
              <div key={l} className="flex-1 bg-black/70 backdrop-blur px-4 py-3">
                <p className="font-display text-sm">{v}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
